import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiPost, apiGet } from 'src/api/client';
import { RootState } from 'src/store';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';
// eslint-disable-next-line
import { MotifItem, TypeCanal } from '../app/appSlice';
import { CustomResponseType, RequestStatus } from '../auth/authSlice';

export enum UserCountry {
  FR = 'FR',
  IT = 'IT',
  ES = 'ES',
  DE = 'DE',
  BE = 'BE',
  NL = 'NL'
}
export enum UserCivilite {
  MME = 'MME',
  MR = 'MR'
}
export enum Direction {
  ASC = 'asc',
  DESC = 'desc'
}

export enum CanalValues {
  BMCE_CLIENT = 'BMCE_CLIENT',
  BES_CLIENT = 'BES_CLIENT',
}

export interface UsersFilter {
  countries?: string[];
  status?: string[];
  etatTraitement?: string[];
  step?: string[];
  title?: string;
  client: boolean;
  dateMin?: string;
  dateMax?: string;
  orderBy?: string;
  direction: Direction;
  needVerifiedRib?: boolean | undefined;
  canals?: TypeCanal[]
}

export interface UserProcessing {
  dateInserted: string;
  uuid: string;
  motif?: MotifItem;
  etatTraitement: {
    code: string;
    dateInserted: string;
    disabled: boolean;
    uuid: string;
    value: string;
  };
}

interface User {
  uuid: string;
  disabled: boolean;
  passwordDateReset: string;
  client: boolean;
  oldClient: boolean;
  idAmplitude: string | number;
  phone: string;
  lastname: string;
  firstname: string;
  treatmentStatus: string;
  status: string;
  numDossier: string;
  userProcessing?: UserProcessing;
  dateInserted: string;
  country: {
    uuid: string;
    label_fr: string;
    iso2Alpha: UserCountry;
  };
  canal: TypeCanal;
}

export interface PaginationType {
  currentPage: number;
  totalPage: number;
  totalElements: number;
  size: number;
}

interface UsersState {
  pagination: PaginationType;
  users: User[];
  status: RequestStatus;
  filter: UsersFilter;
  canExport: boolean;
}
const initialState: UsersState = {
  status: 'idle',
  users: [],
  pagination: {
    currentPage: 0,
    totalPage: 0,
    totalElements: 0,
    size: 0
  },
  filter: {
    countries: undefined,
    status: undefined,
    etatTraitement: undefined,
    title: undefined,
    client: true,
    dateMin: undefined,
    dateMax: undefined,
    step: undefined,
    orderBy: 'dateInserted',
    direction: Direction.DESC,
    needVerifiedRib: undefined,
    canals: undefined,
  },
  canExport: true
};

export type HandleFilterType = (
  key: keyof UsersFilter,
  value?: string | Direction | string[]
) => void;

/* Fetch users list */
interface FetchPayload extends UsersFilter {
  pageNumber: number;
}
export const fetchUsersList = createAsyncThunk<
  {
    users: User[];
    totalPages: number;
    totalElements: number;
    size: number;
  },
  FetchPayload,
  {
    rejectValue: CustomResponseType;
  }
>('users/list', async (payload: FetchPayload, thunkApi) => {
  const response = await apiPost('/customer/all', payload);
  if (response.data.users) {
    return response.data;
  }

  return thunkApi.rejectWithValue({
    code: response.data.code,
    message: 'error'
  });
});

// export list
export const exportList = createAsyncThunk('users/exportUsers', async (payload: { dateMin: string; dateMax: string; }) => {
  const dateStart = encodeURIComponent(`${payload.dateMin} 00:00:00`);
  const dateEnd = encodeURIComponent(`${payload.dateMax} 23:59:59`);
  const { data } = await apiGet(`/documents/extract?dateStart=${dateStart}&dateEnd=${dateEnd}`, { responseType: 'blob' });
  saveAs(data, `export-client-${format(new Date(), 'dd-MM-yyyy-HH:mm')}.xlsx`);
});

/* USERS SLICE */
const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    changePage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    resetFilter: (state) => {
      state.filter = initialState.filter;
      state.pagination.currentPage = 0;
    },
    applyFilter: (
      state,
      action: PayloadAction<{
        key: keyof UsersFilter;
        value: string | Direction | string[] | boolean;
      }>
    ) => {
      if (action.payload.key === 'needVerifiedRib') {
        // @ts-expect-error: Type 'string' is not assignable to type 'Direction'
        state.filter[action.payload.key] = action.payload.value;
        // set client to FALSE to get only prospect that has benef not verified
        state.filter.client = false;
      } else {
        // @ts-expect-error: Type 'string' is not assignable to type 'Direction'
        state.filter[action.payload.key] = action.payload.value;
      }
    },
    resetCanExport: (state) => {
      state.canExport = true;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUsersList.pending, (state) => {
      state.status = 'loading';
    });

    builder.addCase(fetchUsersList.fulfilled, (state, action) => {
      state.users = action.payload.users;
      state.pagination.totalPage = action.payload.totalPages;
      state.pagination.totalElements = action.payload.totalElements;
      state.pagination.size = action.payload.size;
      state.status = 'idle';
    });

    builder.addCase(fetchUsersList.rejected, (state) => {
      state.status = 'error';
    });

    builder.addCase(exportList.fulfilled, (state) => {
      state.status = 'idle';
      state.canExport = false;
    });

    builder.addCase(exportList.pending, (state) => {
      state.status = 'loading';
    });

    builder.addCase(exportList.rejected, (state) => {
      state.status = 'idle';
      state.canExport = false;
    });
  }
});

// selectors
export const selectUsersStatus = (state: RootState) => state.users.status;
export const selectUsersList = (state: RootState) => state.users.users;
export const selectUsersPagination = (state: RootState) => state.users.pagination;
export const selectUsersFilter = (state: RootState) => state.users.filter;
export const selectUsersExport = (state: RootState) => state.users.canExport;

// change page action creator
export const { changePage, applyFilter, resetFilter, resetCanExport } = usersSlice.actions;
export default usersSlice.reducer;
